import { Eye, EyeOff, Loader, Lock, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { acceptInvitation, validateInvitation } from '../api/invitations';
import AuthLayout from '../components/AuthLayout';
import type { Invitation } from '../types/invitation';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [requiresAccountCreation, setRequiresAccountCreation] =
    useState<boolean>(true);

  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });

  const token = searchParams.get('token');

  const validateToken = useCallback(async () => {
    try {
      const result = await validateInvitation(token!);
      if (result.valid && result.invitation) {
        setInvitation(result.invitation);
        setRequiresAccountCreation(result.requiresAccountCreation);
        setFormData((prev) => ({ ...prev, name: result.invitation!.name }));
      } else {
        setError(result.error || 'Invalid or expired invitation');
      }
    } catch {
      setError('Failed to validate invitation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token, validateToken]);

  // Validation schema - conditional based on user existence
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters')
      .label('Name'),
    password: requiresAccountCreation
      ? Yup.string()
          .required('Password is required')
          .min(8, 'Password should have at least 8 characters')
          .max(18, 'Password can have at most 18 characters')
          .matches(
            /[A-Z]/,
            'Password must contain at least 1 upper case letter'
          )
          .matches(
            /[a-z]/,
            'Password must contain at least 1 lower case letter'
          )
          .matches(
            /[!@#$%^&*()_\-+=<>?]/,
            'Password must contain at least 1 special character'
          )
          .matches(/\d+/, 'Password must contain at least 1 number')
          .label('Password')
      : Yup.string().notRequired(),
    confirmPassword: requiresAccountCreation
      ? Yup.string()
          .required('Confirm password is required')
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .label('Confirm password')
      : Yup.string().notRequired(),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      // Validate using Yup schema
      await validationSchema.validate(formData, { abortEarly: false });
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        setError(validationError.errors[0]); // Show first validation error
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      await acceptInvitation({
        token: token!,
        password: formData.password,
        name: formData.name,
        isExistingUser: !requiresAccountCreation,
      });

      window.location.href = '/';
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to accept invitation'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return <AuthLayout loading={true} loadingText="Validating invitation..." />;
  }

  // Error state for invalid invitation
  if (error && !invitation) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-dark mb-3">
            Invalid Invitation
          </h2>
          <p className="text-medium mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
          >
            Go Home
          </a>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome to the Team!"
      subtitle={
        <span>
          You've been invited to join{' '}
          <span className="font-semibold">{invitation?.companyName}</span>
          <br />
          <span className="text-grey10 text-xs">
            by {invitation?.invitedByName}
          </span>
        </span>
      }
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-dangerLight border border-danger/20 text-danger text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Email Field (Disabled) */}
        <div>
          <label className="block text-sm font-semibold text-dark mb-2">
            Email Address
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-grey10" />
            <input
              type="email"
              value={invitation?.email || ''}
              className="w-full pl-10 pr-4 py-3 border border-grey rounded-lg bg-gray-50 text-grey10 cursor-not-allowed"
              disabled
              readOnly
            />
          </div>
        </div>

        {/* Name Field */}
        <div>
          <label className="block text-sm font-semibold text-dark mb-2">
            Full Name
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-grey10" />
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full pl-10 pr-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your full name"
              required
            />
          </div>
        </div>

        {requiresAccountCreation && (
          <>
            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-dark mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-grey10" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full pl-10 pr-12 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Create a password"
                  required
                  minLength={8}
                  maxLength={18}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey10 hover:text-dark"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-semibold text-dark mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-grey10" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="w-full pl-10 pr-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Confirm your password"
                  required
                  minLength={8}
                  maxLength={18}
                />
              </div>
            </div>
          </>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary text-white py-3 !mt-6 rounded-lg font-semibold hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {submitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin mr-2" />
              {requiresAccountCreation
                ? 'Creating Account...'
                : 'Accepting Invitation...'}
            </>
          ) : requiresAccountCreation ? (
            'Accept Invitation & Create Account'
          ) : (
            'Accept Invitation'
          )}
        </button>
      </form>

      {/* Footer */}
      {requiresAccountCreation && (
        <div className="text-center mt-6">
          <p className="text-xs text-grey10">
            By creating an account, you agree to our terms & conditions and
            privacy policy.
          </p>
        </div>
      )}
    </AuthLayout>
  );
};

export default AcceptInvitePage;
